import { useState, useCallback } from 'react';
import { captureAskCircleError } from '@/lib/sentry';

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask-circle-chat`;

export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: any;
}

export function useChatStream() {
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(
    async (
      conversationId: string,
      messages: Message[],
      onDelta: (chunk: string, metadata?: any) => void,
      onDone: () => void
    ) => {
      setIsStreaming(true);
      setError(null);

      try {
        const response = await fetch(CHAT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            conversationId,
            messages,
          }),
        });

        if (!response.ok) {
          if (response.status === 429) {
            throw new Error('Rate limit exceeded. Please try again later.');
          }
          if (response.status === 402) {
            throw new Error('Payment required. Please contact support.');
          }
          if (response.status === 403) {
            // Quota exceeded - parse response for details
            const quotaError = await response.json();
            const error: any = new Error(quotaError.message || 'Daily message limit reached');
            error.quotaError = quotaError;
            throw error;
          }
          throw new Error('Failed to get response');
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let textBuffer = '';
        let streamDone = false;

        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) break;

          textBuffer += decoder.decode(value, { stream: true });

          let newlineIndex: number;
          while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
            let line = textBuffer.slice(0, newlineIndex);
            textBuffer = textBuffer.slice(newlineIndex + 1);

            if (line.endsWith('\r')) line = line.slice(0, -1);
            if (line.startsWith(':') || line.trim() === '') continue;
            if (!line.startsWith('data: ')) continue;

            const jsonStr = line.slice(6).trim();
            if (jsonStr === '[DONE]') {
              streamDone = true;
              break;
            }

            try {
              const parsed = JSON.parse(jsonStr);
              const content = parsed.choices?.[0]?.delta?.content;
              const metadata = parsed.metadata;
              if (content) onDelta(content, metadata);
            } catch {
              textBuffer = line + '\n' + textBuffer;
              break;
            }
          }
        }

        onDone();
      } catch (err: any) {
        console.error('Chat stream error:', err);
        
        captureAskCircleError(err, {
          conversationId,
          action: 'chat_stream',
        });
        
        setError(err.message || 'Failed to send message');
      } finally {
        setIsStreaming(false);
      }
    },
    []
  );

  return {
    sendMessage,
    isStreaming,
    error,
  };
}
